import { useAtomValue } from 'jotai';
import { InsertCustomPriceListMutationVariables, useInsertCustomPriceListMutation } from '../../../mutations/__generated__/insertCustomPriceList.generated';
import { useUpdateContractsByReferenceIdMutation } from '../../../mutations/__generated__/updateContractsByReferenceId.generated';
import { ContractsAsBuyerDocument } from '../../../queries/__generated__/contractsAsBuyer.generated';
import { ContractsAsSupplierDocument } from '../../../queries/__generated__/contractsAsSupplier.generated';
import { CustomPriceListsByCompanyIdDocument } from '../../../queries/__generated__/customPriceListsByCompanyId.generated';
import { contractsAtom } from '../../../state';

export interface UpsertCustomPriceListMutationVariables {
  insertCustomPriceListVariables: InsertCustomPriceListMutationVariables;
  relatedContractIds?: string[];
  deletedContractIds?: string[];
}

export interface UseUpsertCustomPriceListMutation {
  upsertCustomPriceListMutation: (params: UpsertCustomPriceListMutationVariables) => Promise<string | undefined>;
}

export default function UseUpsertCustomPriceListMutation(): UseUpsertCustomPriceListMutation {
  const [insertCustomPriceList] = useInsertCustomPriceListMutation({ refetchQueries: [CustomPriceListsByCompanyIdDocument, ContractsAsSupplierDocument], awaitRefetchQueries: true });
  const [updateContracts] = useUpdateContractsByReferenceIdMutation({ refetchQueries: [CustomPriceListsByCompanyIdDocument] });
  const contracts = useAtomValue(contractsAtom);

  const upsertCustomPriceListMutation = async ({ insertCustomPriceListVariables, relatedContractIds, deletedContractIds }: UpsertCustomPriceListMutationVariables): Promise<string | undefined> => {
    const updateContractRefetchQueries = [ContractsAsBuyerDocument, ContractsAsSupplierDocument];
    const { data: insertCustomPriceListData } = await insertCustomPriceList({ variables: insertCustomPriceListVariables });
    const newCustomPriceListId = insertCustomPriceListData?.insert_custom_price_list_one?.id;

    if (newCustomPriceListId) {
      if (relatedContractIds?.length) {
        const relatedContractReferenceIds = relatedContractIds
          .map((contractId) => contracts.find(({ id }) => id === contractId)?.reference_id)
          .filter((referenceId) => !!referenceId) as string[];

        await updateContracts({
          variables: {
            referenceIds: relatedContractReferenceIds,
            input: {
              custom_price_list_id: newCustomPriceListId,
              discount: 0,
            },
          },
          refetchQueries: !deletedContractIds?.length ? updateContractRefetchQueries : undefined,
          awaitRefetchQueries: true,
        });
      }

      if (deletedContractIds?.length) {
        const deletedContractReferenceIds = deletedContractIds
          .map((contractId) => contracts.find(({ id }) => id === contractId)?.reference_id)
          .filter((referenceId) => !!referenceId) as string[];

          await updateContracts({
          variables: {
            referenceIds: deletedContractReferenceIds!,
            input: {
              custom_price_list_id: null,
              discount: 0,
            },
          },
          refetchQueries: updateContractRefetchQueries,
          awaitRefetchQueries: true,
        });
      }
    }

    return newCustomPriceListId;
  };

  return {
    upsertCustomPriceListMutation,
  };
}
